import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Wifi, Plus, Edit2, Trash2, Save, X, Eye, EyeOff, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { useTranslation } from 'react-i18next';

function WifiManagement() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showPassword, setShowPassword] = useState({});
    const [formData, setFormData] = useState({
        ssid: '',
        password: '',
        security: 'WPA2'
    });
    const [migratedFromLocalStorage, setMigratedFromLocalStorage] = useState(false);

    // Migrate localStorage data to database on first load
    useEffect(() => {
        if (!migratedFromLocalStorage) {
            const savedNetworks = localStorage.getItem('wifiNetworks');
            if (savedNetworks) {
                try {
                    const networks = JSON.parse(savedNetworks);
                    // Filter out networks that came from devices (only migrate manually added ones)
                    const manualNetworks = networks.filter(n => !n.fromDevice);

                    if (manualNetworks.length > 0) {
                        // Migrate each network to the database
                        manualNetworks.forEach(network => {
                            createNetworkMutation.mutate({
                                ssid: network.ssid,
                                password: network.password || '',
                                security: network.security || 'WPA2'
                            }, {
                                onSuccess: () => {
                                    console.log(`Migrated network: ${network.ssid}`);
                                }
                            });
                        });

                        // Clear localStorage after migration
                        localStorage.removeItem('wifiNetworks');
                        toast.success(t('wifi.migrated', 'WiFi networks migrated to database'));
                    }
                } catch (e) {
                    console.error('Failed to migrate WiFi networks:', e);
                }
            }
            setMigratedFromLocalStorage(true);
        }
    }, [migratedFromLocalStorage]);

    // Fetch WiFi networks from API
    const { data: wifiNetworks = [], isLoading, refetch } = useQuery(
        'wifi-networks',
        () => apiService.getWifiNetworks(),
        {
            select: (response) => {
                // API returns { success: true, networks: [...], count: ... }
                if (response?.data?.networks) {
                    return response.data.networks;
                }
                if (response?.networks) {
                    return response.networks;
                }
                if (Array.isArray(response?.data)) {
                    return response.data;
                }
                if (Array.isArray(response)) {
                    return response;
                }
                return [];
            },
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            onError: (error) => {
                toast.error(t('wifi.loadError', 'Failed to load WiFi networks'));
                console.error('Failed to load WiFi networks:', error);
            }
        }
    );

    // Create network mutation
    const createNetworkMutation = useMutation(
        (networkData) => apiService.createWifiNetwork(networkData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('wifi-networks');
                toast.success(t('wifi.networkAdded', 'WiFi network added'));
                handleCancel();
            },
            onError: (error) => {
                const message = error.response?.data?.message || error.message;
                toast.error(t('wifi.addError', { message, defaultValue: `Failed to add network: ${message}` }));
            }
        }
    );

    // Update network mutation
    const updateNetworkMutation = useMutation(
        ({ id, data }) => apiService.updateWifiNetwork(id, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('wifi-networks');
                toast.success(t('wifi.networkUpdated', 'WiFi network updated'));
                handleCancel();
            },
            onError: (error) => {
                const message = error.response?.data?.message || error.message;
                toast.error(t('wifi.updateError', { message, defaultValue: `Failed to update network: ${message}` }));
            }
        }
    );

    // Delete network mutation
    const deleteNetworkMutation = useMutation(
        (networkId) => apiService.deleteWifiNetwork(networkId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('wifi-networks');
                toast.success(t('wifi.networkRemoved', 'WiFi network removed'));
            },
            onError: (error) => {
                const message = error.response?.data?.message || error.message;
                toast.error(t('wifi.deleteError', { message, defaultValue: `Failed to delete network: ${message}` }));
            }
        }
    );

    const handleAdd = () => {
        if (!formData.ssid) {
            toast.error(t('wifi.ssidRequired', 'SSID is required'));
            return;
        }

        if (editingId) {
            // Update existing network
            updateNetworkMutation.mutate({
                id: editingId,
                data: formData
            });
        } else {
            // Create new network
            createNetworkMutation.mutate({
                ssid: formData.ssid,
                password: formData.password,
                security: formData.security
            });
        }
    };

    const handleDelete = (network) => {
        if (network.device_count > 0) {
            if (!window.confirm(t('wifi.confirmDelete', { count: network.device_count, defaultValue: `This network is used by ${network.device_count} device(s). Are you sure?` }))) {
                return;
            }
        }
        deleteNetworkMutation.mutate(network.id);
    };

    const handleEdit = (network) => {
        setEditingId(network.id);
        setShowAddForm(true);
        setFormData({
            ssid: network.ssid,
            password: network.password || '',
            security: network.security || 'WPA2'
        });
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingId(null);
        setFormData({ ssid: '', password: '', security: 'WPA2' });
    };

    const togglePasswordVisibility = (id) => {
        setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
            {/* Modern Header */}
            <div className="card">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-200 via-cyan-300 to-teal-300 flex items-center justify-center shadow-lg">
                            <Wifi className="w-6 h-6 text-blue-900" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                {t('wifi.title', 'WiFi Management')}
                            </h1>
                            <p className="text-sm sm:text-base text-gray-600 mt-1">
                                {t('wifi.subtitle', 'Manage saved WiFi networks for easy device configuration')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            if (showAddForm) {
                                handleCancel();
                            } else {
                                setShowAddForm(true);
                            }
                        }}
                        className="btn-primary flex items-center space-x-2 whitespace-nowrap"
                    >
                        {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        <span>{showAddForm ? t('wifi.cancel', 'Cancel') : t('wifi.addNetwork', 'Add Network')}</span>
                    </button>
                </div>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
                <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {editingId ? t('wifi.editNetwork', 'Edit WiFi Network') : t('wifi.addNewNetwork', 'Add New WiFi Network')}
                    </h3>
                    <form onSubmit={(e) => { e.preventDefault(); handleAdd(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('wifi.ssid', 'SSID (Network Name)')} *
                                </label>
                                <input
                                    type="text"
                                    value={formData.ssid}
                                    onChange={(e) => setFormData({ ...formData, ssid: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                    placeholder={t('wifi.placeholder.ssid', 'My WiFi Network')}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('wifi.security', 'Security')}
                                </label>
                                <select
                                    value={formData.security}
                                    onChange={(e) => setFormData({ ...formData, security: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                >
                                    {['WPA2', 'WPA3', 'WPA', 'Open'].map((option) => (
                                        <option key={option} value={option}>
                                            {t(`wifi.securityOptions.${option.toLowerCase()}`, option)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {formData.security !== 'Open' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('wifi.password', 'Password')}
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                        placeholder={t('wifi.placeholder.password', 'Network password')}
                                        autoComplete="new-password"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="inline-flex items-center gap-2 rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
                            >
                                <X className="h-4 w-4" />
                                {t('wifi.cancel', 'Cancel')}
                            </button>
                            <button
                                type="submit"
                                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                            >
                                <Save className="h-4 w-4" />
                                {editingId ? t('wifi.update', 'Update Network') : t('wifi.saveNetwork', 'Save Network')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Networks List */}
            {isLoading ? (
                <div className="text-center py-12">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                    <p className="mt-2 text-gray-600">{t('wifi.loading', 'Loading networks...')}</p>
                </div>
            ) : wifiNetworks.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Wifi className="h-12 w-12 mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-600">{t('wifi.noNetworks', 'No WiFi networks configured yet')}</p>
                    <p className="text-sm text-gray-500">{t('wifi.addToGetStarted', 'Add a network to get started')}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {wifiNetworks.map((network) => (
                        <div
                            key={network.id}
                            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                        >
                            {/* View Mode */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4 flex-1">
                                    <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                                        <Wifi className="h-6 w-6 text-indigo-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-lg font-semibold text-gray-900">{network.ssid}</h3>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                                                <Shield className="h-3 w-3" />
                                                {network.security || 'WPA2'}
                                            </span>
                                        </div>
                                        <div className="mt-1 flex items-center gap-4 text-sm text-gray-600">
                                            {network.security !== 'Open' && (
                                                <div className="flex items-center gap-2">
                                                    {network.from_device ? (
                                                        <span className="font-mono text-gray-400">
                                                            ••••••••
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => togglePasswordVisibility(network.id)}
                                                            className="flex items-center gap-1 hover:text-gray-900"
                                                        >
                                                            {showPassword[network.id] ? (
                                                                <EyeOff className="h-4 w-4" />
                                                            ) : (
                                                                <Eye className="h-4 w-4" />
                                                            )}
                                                            <span className="font-mono">
                                                                {showPassword[network.id]
                                                                    ? (network.password || t('wifi.noPassword', 'No password'))
                                                                    : '••••••••'
                                                                }
                                                            </span>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {network.device_count > 0 && (
                                                <span className="text-green-600 font-medium">
                                                    {t('wifi.usedByDevices', { count: network.device_count, defaultValue: `Used by ${network.device_count} device${network.device_count > 1 ? 's' : ''}` })}
                                                </span>
                                            )}
                                        </div>
                                        {network.device_names && network.device_names.length > 0 && (
                                            <div className="mt-2 text-xs text-gray-500">
                                                {t('wifi.devices', 'Devices')}: {network.device_names.slice(0, 3).join(', ')}
                                                {network.device_names.length > 3 && ` ${t('wifi.moreDevices', { count: network.device_names.length - 3, defaultValue: `+${network.device_names.length - 3} more` })}`}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleEdit(network)}
                                        className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                        {t('wifi.edit', 'Edit')}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(network)}
                                        className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        {t('wifi.delete', 'Delete')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default WifiManagement;
